import { browser } from '$app/environment';
import { tick } from 'svelte';

function getStorage(): Storage | null {
	if (!browser || typeof window === 'undefined') {
		return null;
	}

	const storage = window.localStorage;

	if (
		typeof storage?.getItem !== 'function' ||
		typeof storage?.setItem !== 'function' ||
		typeof storage?.removeItem !== 'function'
	) {
		return null;
	}

	return storage;
}

// https://github.com/Rich-Harris/local-storage-test
export class LocalStorage<T> {
	#key: string;
	#version = $state(0);
	#listeners = 0;
	#value: T | undefined;

	#handler = (e: StorageEvent) => {
		const storage = getStorage();
		if (!storage || e.storageArea !== storage) return;
		if (e.key !== this.#key) return;

		this.#version += 1;
	};

	constructor(key: string, initial?: T) {
		this.#key = key;
		this.#value = initial;

		const storage = getStorage();
		if (storage && storage.getItem(key) === null) {
			storage.setItem(key, JSON.stringify(initial));
		}
	}

	get current(): T {
		this.#version;

		const storage = getStorage();
		const root = storage ? JSON.parse(storage.getItem(this.#key) as any) : this.#value;

		const proxies = new WeakMap();

		const proxy = (value: unknown) => {
			if (typeof value !== 'object' || value === null) {
				return value;
			}

			let p = proxies.get(value);

				if (!p) {
					p = new Proxy(value, {
						get: (target, property) => {
							this.#version;
							return proxy(Reflect.get(target, property));
						},
						set: (target, property, value) => {
							this.#version += 1;
							Reflect.set(target, property, value);

							if (storage) {
								storage.setItem(this.#key, JSON.stringify(root));
							}

							return true;
						}
					});

				proxies.set(value, p);
			}

			return p;
		};

		if ($effect.tracking()) {
			$effect(() => {
				if (this.#listeners === 0) {
					window.addEventListener('storage', this.#handler);
				}

				this.#listeners += 1;

				return () => {
					tick().then(() => {
						this.#listeners -= 1;
						if (this.#listeners === 0) {
							window.removeEventListener('storage', this.#handler);
						}
					});
				};
			});
		}

		return proxy(root);
	}

	set current(value: T) {
		const storage = getStorage();
		if (storage) {
			storage.setItem(this.#key, JSON.stringify(value));
		}

		this.#version += 1;
	}
}
